import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Camera, Upload, AlertCircle, CheckCircle, Search, X, Shield, User, LogOut, Zap, Lock, Car, Share2, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import LanguageSelector from './LanguageSelector';
import InstallPrompt from './InstallPrompt';
import LoadingOverlay from './LoadingOverlay';
import CountdownTimer from './CountdownTimer';
import ResultSkeleton from './ResultSkeleton';
import SearchHistory from './SearchHistory';
import useGeolocation from '../hooks/useGeolocation';
import ShareRide from './ShareRide';
import SOSButton from './SOSButton';
import BottomNav from './BottomNav';
import ResultCard from './ResultCard';
import fetchWithTimeout from '../lib/fetchWithTimeout';
import { API_URL as BACKEND_URL } from '../lib/apiUrl';
import { isNativePlatform, isIOS } from '../lib/capacitor';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

// All US states
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

// States with registry verification APIs (TLC/BACP)
const REGISTRY_STATES = ['NY', 'NJ', 'CT', 'IL'];

const FREE_DAILY_LIMIT = 3;

export default function PlateChecker() {
  const { t } = useTranslation();
  const { tier, signOut, isAuthenticated, loading: authLoading, getAccessToken } = useAuth();
  const toast = useToast();
  const { getLocation } = useGeolocation();
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [plateInput, setPlateInput] = useState('');
  const [selectedState, setSelectedState] = useState('NY');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [image, setImage] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const plateInputRef = useRef(null);
  const scanSectionRef = useRef(null);
  const historySectionRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [videoZoom, setVideoZoom] = useState(1);
  const touchStartDistance = useRef(null);
  const [dailyLookups, setDailyLookups] = useState(() => {
    const stored = localStorage.getItem('tlc_daily_lookups');
    if (stored) {
      const { count, date } = JSON.parse(stored);
      if (date === new Date().toDateString()) return count;
    }
    return 0;
  });

  // Check for terms acceptance and banner dismissal on load
  useEffect(() => {
    const termsAccepted = localStorage.getItem('tlc_terms_accepted');
    if (termsAccepted === 'true') {
      setHasAcceptedTerms(true);
    }
    const bannerDismissed = localStorage.getItem('tlc_banner_dismissed');
    if (bannerDismissed === 'true') {
      setShowBanner(false);
    }
  }, []);

  // Handle banner dismissal
  const handleDismissBanner = () => {
    localStorage.setItem('tlc_banner_dismissed', 'true');
    setShowBanner(false);
  };

  // Handle terms acceptance
  const handleAcceptTerms = () => {
    localStorage.setItem('tlc_terms_accepted', 'true');
    setHasAcceptedTerms(true);
  };

useEffect(() => {
  if (showCamera && stream && videoRef.current) {
    videoRef.current.srcObject = stream;

    videoRef.current.onloadedmetadata = () => {
      videoRef.current.play().catch(() => {});
    };
  }
  // Reset zoom when camera closes
  if (!showCamera) {
    setVideoZoom(1);
  }
}, [showCamera, stream]);

  // Pinch-zoom handlers for video
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      touchStartDistance.current = getDistance(e.touches);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDistance.current) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches);
      const scale = currentDistance / touchStartDistance.current;
      setVideoZoom(prevZoom => {
        const newZoom = prevZoom * scale;
        // Limit zoom between 1x and 20x
        return Math.min(Math.max(newZoom, 1), 20);
      });
      touchStartDistance.current = currentDistance;
    }
  };

  const handleTouchEnd = () => {
    touchStartDistance.current = null;
  };

  // Handle logout
  const handleLogout = async () => {
    await signOut();
    setResult(null);
    setImage(null);
    setPlateInput('');
  };

  // Start camera — native Capacitor camera on iOS/Android, web getUserMedia on desktop
const startCamera = async () => {
  if (isNativePlatform()) {
    // Use Capacitor Camera plugin for native — opens system camera UI
    try {
      const photo = await CapCamera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: 3840,
        height: 2160,
        correctOrientation: true,
      });

      if (photo.dataUrl) {
        setImage(photo.dataUrl);
        setResult(null);
      }
    } catch (err) {
      // User cancelled — don't show error
      if (err.message && !err.message.includes('cancel')) {
        toast.error('Camera Error: ' + err.message);
      }
    }
    return;
  }

  // Web fallback — live preview with getUserMedia
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Camera not available. Please use HTTPS or access via localhost.');
      return;
    }

    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 3840 },
        height: { ideal: 2160 },
      },
      audio: false
    });

    setStream(mediaStream);
    setShowCamera(true);
  } catch (err) {
    toast.error('Camera Error: ' + err.message);
  }
};

  // Stop camera
const stopCamera = () => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop());
  }
  setStream(null);
  setShowCamera(false);
};

  // Capture photo from camera
  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;

    if (canvas && video) {
      // Haptic feedback on capture
      if (isNativePlatform()) {
        Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
      } else if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImage(e.target.result);
          setResult(null);
        };
        reader.readAsDataURL(blob);
        stopCamera();
      }, 'image/jpeg', 0.95);
    }
  };

  // Pick photo from library — native Capacitor or web file input
  const pickFromLibrary = async () => {
    if (isNativePlatform()) {
      try {
        const photo = await CapCamera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Photos,
          width: 3840,
          height: 2160,
          correctOrientation: true,
        });
        if (photo.dataUrl) {
          setImage(photo.dataUrl);
          setResult(null);
        }
      } catch (err) {
        if (err.message && !err.message.includes('cancel')) {
          toast.error('Photo Error: ' + err.message);
        }
      }
      return;
    }
    // Web fallback — open file picker
    fileInputRef.current?.click();
  };

  // Handle image upload (web file input)
  const handleImageUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
        setResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Extract and check plate from image
  const extractPlateFromImage = async () => {
    if (!image) return;

    setExtracting(true);
    setResult(null);

    try {
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/read-plate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to read plate');

      if (data.plate) {
        setPlateInput(data.plate);
        // Include image for color detection
        await checkPlateNumber(data.plate, true);
      } else {
        toast.warning(data.error || 'Could not read plate. Try a closer, clearer photo or enter manually.');
      }
    } catch (error) {
      toast.error('Error reading plate: ' + error.message);
    } finally {
      setExtracting(false);
    }
  };

  // Check plate
  const checkPlateNumber = async (plate, includeImage = false) => {
    const plateToCheck = plate || plateInput;

    if (!plateToCheck.trim()) {
      toast.warning('Please enter a plate number (e.g., ABC1234)');
      return;
    }

    // Basic format validation
    const cleanPlate = plateToCheck.trim().toUpperCase();
    if (cleanPlate.length < 2 || cleanPlate.length > 8) {
      toast.warning('Plate should be 2-8 characters (e.g., ABC1234, T123456C)');
      return;
    }

    // Check for invalid characters
    if (!/^[A-Z0-9]+$/.test(cleanPlate)) {
      toast.warning('Plate should only contain letters and numbers');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Get location in parallel with the lookup (for logged-in users)
      const locationPromise = isAuthenticated ? getLocation() : Promise.resolve(null);

      // Build request body - include image for color detection if available
      const requestBody = {
        plate: cleanPlate,
        state: selectedState,
        enhanced: true // Request enhanced lookup if available
      };

      // Include image for color detection when available
      if (includeImage && image) {
        requestBody.image = image;
      }

      // Build headers - include auth token if logged in
      const headers = { 'Content-Type': 'application/json' };
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetchWithTimeout(`${BACKEND_URL}/api/lookup-enhanced`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      // Handle rate limit
      if (response.status === 429) {
        setResult({
          error: true,
          rate_limited: true,
          message: data.error || 'Daily lookup limit reached',
          limit: data.limit,
          used: data.used,
          tier: data.tier,
          resets_at: data.resets_at
        });
        return;
      }

      if (!response.ok) throw new Error(data.error || 'Lookup failed');

      setResult(data);

      // Track daily lookups for free tier usage counter
      if (tier === 'free') {
        const newCount = dailyLookups + 1;
        setDailyLookups(newCount);
        localStorage.setItem('tlc_daily_lookups', JSON.stringify({ count: newCount, date: new Date().toDateString() }));
      }

      // Haptic feedback on result
      if (navigator.vibrate) {
        navigator.vibrate(data.is_tlc_vehicle ? [50] : [50, 30, 50]);
      }

      // Save to search history for subscribers only
      const isSubscriber = ['starter', 'starter_annual', 'pro', 'pro_annual'].includes(tier);
      if (isAuthenticated && token && !data.error && isSubscriber) {
        const location = await locationPromise;
        saveSearchHistory(token, {
          plate: cleanPlate,
          state: selectedState,
          was_licensed: data.is_tlc_vehicle,
          vehicle_info: data.vehicle_info ? {
            year: data.vehicle_info.year,
            make: data.vehicle_info.make,
            model: data.vehicle_info.model,
            color: data.vehicle_info.color
          } : null,
          car_image: data.car_image || null,
          latitude: location?.latitude,
          longitude: location?.longitude,
          location_name: location?.locationName
        });
      }
    } catch (error) {
      toast.error('Error connecting to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Save search to history (fire and forget)
  const saveSearchHistory = async (token, historyData) => {
    try {
      await fetch(`${BACKEND_URL}/api/search-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(historyData)
      });
    } catch (err) {
      // Don't show error to user - history is a nice-to-have
    }
  };


  // Show terms acceptance screen if not accepted
  if (!hasAcceptedTerms) {
    return (
      <div className="app-container">
        <div className="app-wrapper">
          <div className="app-card">
            <div className="app-header">
              <div className="header-top">
                <h1>
                  <Shield className="header-icon" />
                  {t('terms.title')}
                </h1>
                <div className="header-actions">
                  <LanguageSelector />
                </div>
              </div>
              <p>{t('terms.subtitle')}</p>
            </div>
            <div className="terms-section">
              <div className="terms-content">
                <h3>{t('terms.heading')}</h3>
                <p>{t('terms.disclaimer')}</p>
                <h4>{t('terms.byUsing')}</h4>
                <ul>
                  <li>{t('terms.agree1')}</li>
                  <li>{t('terms.agree2')}</li>
                  <li>{t('terms.agree3')}</li>
                  <li>{t('terms.agree4')}</li>
                </ul>
              </div>
              <label className="terms-checkbox">
                <input
                  type="checkbox"
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                />
                <span>
                  {t('terms.checkboxText')}{' '}
                  <Link to="/terms" target="_blank">{t('terms.termsLink')}</Link>
                  {' '}{t('terms.and')}{' '}
                  <Link to="/privacy" target="_blank">{t('terms.privacyLink')}</Link>
                </span>
              </label>
              <button
                onClick={handleAcceptTerms}
                className="btn btn-yellow btn-large"
                disabled={!termsChecked}
              >
                <CheckCircle size={18} /> {t('terms.accept')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="app-container">
        <div className="app-wrapper">
          <div className="app-card">
            <div className="app-header">
              <h1>{t('app.loading')}</h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <LoadingOverlay isLoading={loading || extracting} />
      <InstallPrompt />
      <SOSButton />
      <BottomNav
        onScrollToScan={() => scanSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
        onScrollToHistory={() => historySectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
      />
      <div className="app-wrapper">
        <div className="app-card">

          {/* Header */}
          <div className="app-header">
            <div className="header-top">
              <h1>
                <a href="https://wiredcop.com" target="_blank" rel="noopener noreferrer">
                  <img src="/logo192.png" alt="WiredCop" className="header-logo" />
                </a>
                {t('app.title')}
              </h1>
              <div className="header-actions">
                <LanguageSelector />
                {isAuthenticated ? (
                  <>
                    <Link to="/account" className="header-link">
                      <User size={16} /> {tier !== 'free' && <span className="tier-badge-small">{tier}</span>}
                    </Link>
                    <button onClick={handleLogout} className="logout-btn" aria-label="Sign out">
                      <LogOut size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    {!isIOS() && (
                      <Link to="/pricing" className="header-link">{t('nav.pricing')}</Link>
                    )}
                    <Link to="/login" className="btn btn-yellow btn-small">{t('nav.signIn')}</Link>
                  </>
                )}
              </div>
            </div>
            <p className="tagline-with-cities">
              <Car size={18} className="car-icon" />
              Scan. Verify. Ride safe.
            </p>
          </div>

          {/* Welcome Banner - World Cup 2026 (only for free users) */}
          {showBanner && !showCamera && tier === 'free' && (
            <div className="welcome-banner">
              <div className="banner-content">
                <span className="banner-icon">⚽</span>
                <div className="banner-text">
                  <strong>{t('banner.title')}</strong>
                  <p>{t('banner.subtitle')}</p>
                  <span className="banner-free-info">{t('banner.freeInfo')}</span>
                </div>
              </div>
              <button onClick={handleDismissBanner} className="banner-dismiss">
                {t('banner.dismiss')}
              </button>
            </div>
          )}


          {/* Camera View */}
          {showCamera && (
            <div className="camera-view">
              <div
                className="video-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ transform: `scale(${videoZoom})` }}
                />
              </div>
              {/* Frame guide overlay - outside video container so it doesn't zoom */}
              <div className="camera-frame-guide">
                <div className="frame-box">
                  <div className="frame-corner top-left"></div>
                  <div className="frame-corner top-right"></div>
                  <div className="frame-corner bottom-left"></div>
                  <div className="frame-corner bottom-right"></div>
                </div>
                <span className="frame-hint">{t('scan.alignHint')}</span>
              </div>
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div className="camera-controls">
                <button onClick={capturePhoto} className="btn btn-yellow" aria-label="Capture photo">
                  {t('scan.capturePhoto')}
                </button>
                <button onClick={stopCamera} className="btn btn-red" aria-label="Close camera">
                  <X size={18} /> {t('scan.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Main Content - No Camera, No Image */}
          {!showCamera && !image && (
            <div className="main-content">
              {/* Scan Section */}
              <div className="scan-section" ref={scanSectionRef}>
                <h3>{t('scan.title')}</h3>
                <p>{t('scan.subtitle')}</p>

                <div className="scan-buttons">
                  <button onClick={startCamera} className="btn btn-yellow btn-camera-primary">
                    <Camera size={22} /> {t('scan.useCamera')}
                  </button>
                  <button onClick={pickFromLibrary} className="btn-upload-link">
                    <Upload size={14} /> {t('scan.uploadPhoto')}
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Manual Entry with State Dropdown */}
              <div className="manual-section">
                <label>{t('manual.label')}</label>
                <div className="manual-input-group">
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="state-select"
                    aria-label="Select state"
                  >
                    {US_STATES.map(state => (
                      <option key={state} value={state}>
                        {state}{REGISTRY_STATES.includes(state) ? '' : ''}
                      </option>
                    ))}
                  </select>
                  <input
                    ref={plateInputRef}
                    type="text"
                    value={plateInput}
                    onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && checkPlateNumber()}
                    placeholder={t('manual.placeholder')}
                    maxLength="10"
                    autoFocus
                    aria-label="License plate number"
                  />
                  <button
                    onClick={() => checkPlateNumber()}
                    disabled={loading}
                    className="btn btn-blue"
                    aria-label="Check plate number"
                  >
                    {loading ? t('manual.checking') : <><Search size={18} /> {t('manual.check')}</>}
                  </button>
                </div>

                {/* Free tier usage counter */}
                {tier === 'free' && dailyLookups > 0 && (
                  <div className={`usage-counter ${dailyLookups >= FREE_DAILY_LIMIT ? 'at-limit' : ''}`}>
                    <div className="usage-counter-bar">
                      <div className="usage-counter-fill" style={{ width: `${Math.min((dailyLookups / FREE_DAILY_LIMIT) * 100, 100)}%` }} />
                    </div>
                    <span className="usage-counter-text">
                      {dailyLookups >= FREE_DAILY_LIMIT
                        ? t('limit.reached')
                        : t('limit.usageCount', { used: dailyLookups, total: FREE_DAILY_LIMIT })}
                    </span>
                    {dailyLookups >= FREE_DAILY_LIMIT - 1 && !isIOS() && (
                      <Link to="/pricing" className="usage-upgrade-link">{t('pricing.upgrade')}</Link>
                    )}
                  </div>
                )}

                {/* Non-Registry State Info - only show before a result */}
                {!REGISTRY_STATES.includes(selectedState) && !result && (
                  <div className="unsupported-state-message">
                    <div className="unsupported-header">
                      <Info size={20} />
                      <strong>{t('nonRegistry.infoTitle', { state: selectedState })}</strong>
                    </div>
                    <p>
                      <Trans i18nKey="nonRegistry.description" values={{ state: selectedState }} components={{ strong: <strong /> }} />
                    </p>
                    <div className="unsupported-features">
                      <span><Car size={14} /> {t('nonRegistry.featureVehicleInfo')}</span>
                      <span><Share2 size={14} /> {t('nonRegistry.featureShare')}</span>
                    </div>
                  </div>
                )}

                {/* Search History */}
                <div ref={historySectionRef} />
                <SearchHistory onSelectPlate={(plate, state, historyEntry) => {
                  setPlateInput(plate);
                  setSelectedState(state || 'NY');
                  // Show cached result from history instead of new lookup
                  if (historyEntry) {
                    setResult({
                      is_tlc_vehicle: historyEntry.was_licensed,
                      vehicle_info: historyEntry.vehicle_info,
                      car_image: historyEntry.car_image,
                      plate: plate,
                      from_history: true
                    });
                  }
                }} />
              </div>

              {/* Results */}
              {loading && <ResultSkeleton />}
              {!loading && result && <ResultCard result={result} tier={tier} isAuthenticated={isAuthenticated} />}

              {/* Footer */}
              <div className="app-footer">
                <div className="supported-registries">
                  <span className="registry-label">{t('about.coverage')}</span>
                  <div className="registry-badges">
                    <span className="registry-badge registry-all">{t('about.allStates')}</span>
                    <span className="registry-badge">{t('about.registryNYC')}</span>
                    <span className="registry-badge">{t('about.registryChicago')}</span>
                  </div>
                </div>
                <div className="developed-by">
                  <a href="https://wiredcop.com" target="_blank" rel="noopener noreferrer">
                    <img src="/logo192.png" alt="WiredCop" className="footer-logo" />
                  </a>
                  <div className="developed-by-text">
                    <span>{t('about.developedBy')} <a href="https://wiredcop.com" target="_blank" rel="noopener noreferrer">WiredCop LLC</a></span>
                    <span className="tagline">{t('about.tagline')}</span>
                  </div>
                </div>
                <div className="legal-links">
                  <Link to="/terms">Terms of Service</Link>
                  <span className="separator">|</span>
                  <Link to="/privacy">Privacy Policy</Link>
                </div>
              </div>
            </div>
          )}

          {/* Image Preview */}
          {!showCamera && image && (
            <div className="main-content">
              <div className="image-preview">
                <img src={image} alt="License plate" />
              </div>

              <div className="image-actions">
                <button
                  onClick={extractPlateFromImage}
                  disabled={extracting}
                  className="btn btn-yellow btn-large"
                >
                  {extracting ? t('scan.readingPlate') : t('scan.readPlate')}
                </button>
                <button
                  onClick={() => { setImage(null); setResult(null); setPlateInput(''); }}
                  className="btn btn-gray"
                >
                  {t('scan.clear')}
                </button>
              </div>

              {/* Results */}
              {loading && <ResultSkeleton />}
              {!loading && result && <ResultCard result={result} tier={tier} isAuthenticated={isAuthenticated} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
